import { useQueryWithErrorLog } from '#app/hooks/queries/useQueryWithErrorLog';
import { useMemo } from 'react';
import InvestHelper from '#app/helpers/InvestHelper';
import {
  ActiveConstants,
  configureInvestGridOptimizedQuery,
  investGridInvestsPageOptions,
  type InvestsTradesFilter,
} from 'finhelper';
import { QUERY_KEYS } from '#app/constants/queryKeys';
import { QUERY_CACHE_TIMES } from '#app/constants/queryCacheTimes';
import { buildInvestGridSupplementActives } from '#app/hooks/queries/portfolio/buildInvestGridSupplementActives';
import { enrichGridActivesGroupTypeText } from '#app/hooks/queries/portfolio/portfolioGroupTypeText';

export type { InvestsTradesFilter };

interface UseInvestsQueryParams {
  clientId: number | null;
  requestClientId: number | null;
  currencyId?: string;
  currency?: any;
  accounts?: any[];
  groupType?: number;
  tutorial?: boolean;
  demo?: boolean;
  enabled?: boolean;
  /**
   * Фильтр optimized-index (finhelper `InvestsTradesFilter`, см. investGridOptimizedQuery.ts):
   *  - `open_invest_traded` — открытые invest + property INVEST/INVEST_TRADED
   *  - `open_invest_traded_currency` — + валютные активы (привязка к целям)
   *  - `open_invest_traded_deposit_debt_insurance` — + DEPOSIT/DEBT/СЖ (благосостояние)
   *  - `open_or_short_invest_own_traded` — open или short + INVEST/OWN/INVEST_TRADED («Ценности»)
   *  - `open_no_sell_invest` — без продаж + только INVEST property (не используется в WS)
   *  - `invest-repo-only` — только активы с РЕПО-сделками (не используется в WS)
   *  - `invest-repo-exclude` — как open_invest_traded, но без активов с РЕПО (не используется в WS)
   *  - `case_numbers` — CaseNumbers «Текущая стоимость активов» (legacy active/index OR-фильтры)
   *  - `property_active` / `property_archive` — страница «Счета/Активы/Обязательства»
   */
  tradesFilter?: InvestsTradesFilter;
}

/**
 * Query hook для загрузки инвестиций
 */
export const useInvestsQuery = ({
  clientId,
  requestClientId,
  currencyId = 'none',
  currency,
  accounts = [],
  groupType = ActiveConstants.BY_ACCOUNT,
  tutorial = false,
  demo = false,
  enabled = true,
  tradesFilter = investGridInvestsPageOptions.tradesFilter!,
}: UseInvestsQueryParams) => {
  const queryKey = useMemo(() => {
    const key = [...QUERY_KEYS.invests.all(clientId, requestClientId, currencyId), tradesFilter];
    return key;
  }, [clientId, requestClientId, currencyId, tradesFilter]);

  const isEnabled = enabled && !!clientId && !tutorial && !demo;

  return useQueryWithErrorLog(
    {
      queryKey,
      enabled: isEnabled,
      queryFn: async () => {
        const queryCallback = (query: any, resolve: (value: any) => void) => {
          configureInvestGridOptimizedQuery(query, {
            clientId: clientId as number,
            tradesFilter,
          });
          query.all((response: any) => resolve(response));
        };

        const response = await InvestHelper.loadData(
          clientId,
          requestClientId,
          currencyId,
          accounts,
          currency,
          { current: [] as any[] },
          () => {},
          () => {},
          groupType,
          null,
          queryCallback
        );

        return {
          response,
        };
      },
      staleTime: QUERY_CACHE_TIMES.NORMAL_UPDATE,
      gcTime: QUERY_CACHE_TIMES.NORMAL_UPDATE,
    },
    'useInvestsQuery'
  );
};

/**
 * Hook для получения подготовленных данных инвестиций.
 *
 * `includeRepo` — семантика finhelper (фильтрация/учёт РЕПО при группировке).
 *
 * `packageTradesMode` — `PrepareActivesOptions` в finhelper: `full` — одна строка грида на пакет
 * с полными сделками и `package_trade_buckets`; `split_by_trade_state` — legacy. Если не
 * передан — режим по умолчанию в библиотеке.
 */
export const usePreparedInvests = ({
  investsData,
  accounts = [],
  currency = null,
  requestClientId = null,
  groupType = ActiveConstants.BY_ACCOUNT,
  includeRepo = false,
  packageTradesMode,
  groupTypesById,
}: {
  investsData?: { data?: { response?: { data?: any[] } } };
  accounts?: any[];
  currency?: any;
  requestClientId?: number | null;
  groupType?: number;
  includeRepo?: boolean;
  packageTradesMode?: 'full' | 'split_by_trade_state';
  groupTypesById?: Map<number, string>;
}) => {
  const preparedActives = useMemo<{
    valuationSum: number;
    currencies: any[];
    activesWithoutCurrencies: any[];
    actives: any[];
  }>(() => {
    const apiResponseData = investsData?.data?.response?.data;
    if (!Array.isArray(apiResponseData)) {
      return {
        valuationSum: 0,
        currencies: [],
        activesWithoutCurrencies: [],
        actives: [],
      };
    }

    const apiActives = InvestHelper.transformResponseToActives(apiResponseData);

    const { currencyActives, blockedActives: blockedItems } = buildInvestGridSupplementActives({
      accounts,
      currency,
      requestClientId,
    });

    const rawActives = [...apiActives, ...currencyActives, ...blockedItems];

    enrichGridActivesGroupTypeText(rawActives, groupTypesById);

    return InvestHelper.prepareActives(rawActives, groupType, {
      includeRepo,
      ...(packageTradesMode != null ? { packageTradesMode } : {}),
    });
  }, [
    investsData?.data?.response?.data,
    accounts,
    currency,
    requestClientId,
    groupType,
    includeRepo,
    packageTradesMode,
    groupTypesById,
  ]);

  return preparedActives;
};
